import React from 'react'
import { useParams } from 'react-router-dom'
import { Container, MenuItem, Grid, Typography } from '@mui/material'
import AdminPageHeader from '../../components/admin/shared/AdminPageHeader'
import AdminFormCard from '../../components/admin/shared/AdminFormCard'
import AdminFormActions from '../../components/admin/shared/AdminFormActions'
import AdminFormLoading from '../../components/admin/shared/AdminFormLoading'
import FormErrorSummary from '../../components/admin/shared/FormErrorSummary'
import {
  FieldGroupHeading,
  LabeledSelect,
  LabeledTextField,
} from '../../components/admin/shared/fields'
import { apiClient } from '../../services/api'
import { ValidationSchema } from '../../hooks/useFormValidation'
import { useDataFetching } from '../../hooks/useDataFetching'
import { useSimpleAdminForm } from '../../hooks/useSimpleAdminForm'

interface BDMOption {
  id: number
  name: string
  email: string
  market: string
}

interface RegionFormData {
  region_code: string
  region_name: string
  market: string
  description: string
  default_bdm_id: number | null
}

const initialFormData: RegionFormData = {
  region_code: '',
  region_name: '',
  market: 'ANZ',
  description: '',
  default_bdm_id: null
}

const validationSchema: ValidationSchema = {
  region_code: [{ type: 'required', message: 'Region code is required' }],
  region_name: [{ type: 'required', message: 'Region name is required' }],
  market: [{ type: 'required', message: 'Market is required' }]
}

// Friendly labels for the shared validation summary.
const REGION_FIELD_LABELS: Record<string, string> = {
  region_code: 'Region Code',
  region_name: 'Region Name',
  market: 'Market',
}

const RegionForm: React.FC = () => {
  const { code } = useParams<{ code?: string }>()
  const isEditMode = !!code

  const {
    values,
    errors,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    isSubmitting,
    loading,
    error,
  } = useSimpleAdminForm<RegionFormData>({
    isEditMode,
    resourcePath: '/admin/regions',
    resourceId: code,
    listPath: '/admin/system/regions',
    initialValues: initialFormData,
    validationSchema,
    toPayload: (formValues) => ({
      region_code: formValues.region_code.trim().toUpperCase(),
      region_name: formValues.region_name.trim(),
      market: formValues.market,
      description: formValues.description.trim() || null,
      default_bdm_id: formValues.default_bdm_id || null,
      clear_default_bdm: formValues.default_bdm_id === null,
    }),
    fromResponse: (data) => ({
      region_code: data.region_code || '',
      region_name: data.region_name || '',
      market: data.market || 'ANZ',
      description: data.description || '',
      default_bdm_id: data.default_bdm_id ?? null,
    }),
    saveErrorMessage: 'Failed to save region',
    loadErrorMessage: 'Failed to load region',
  })

  // BDM options are market-specific - refetch whenever the selected market changes.
  const { data: bdmOptionsData } = useDataFetching<BDMOption[]>({
    fetchFn: async () => {
      const params = new URLSearchParams()
      if (values.market) params.append('market', values.market)
      const response = await apiClient.get(`/admin/bdm-options?${params}`)
      return response.data
    },
    dependencies: [values.market],
  })
  const bdmOptions = bdmOptionsData ?? []

  // Market drives the BDM option list, so clear any selected default when it changes.
  const handleMarketChange = (event: any) => {
    setFieldValue('market', event.target.value)
    setFieldValue('default_bdm_id', null)
  }

  if (loading) {
    return <AdminFormLoading />
  }

  const regionName = values.region_name?.trim()

  return (
    <Container maxWidth="md">
      <AdminPageHeader
        pageKey="regionManagement"
        dense
        titleOverride={isEditMode ? 'Edit Region' : 'Add Region'}
        breadcrumbs={[
          { label: 'Admin', to: '/admin' },
          { label: 'System', to: '/admin/system' },
          { label: 'Region Management', to: '/admin/system/regions' },
          ...(isEditMode ? [{ label: regionName || 'Region' }, { label: 'Edit' }] : [{ label: 'Add New' }]),
        ]}
      />

      <FormErrorSummary
        message={error}
        errors={errors as Record<string, string>}
        fieldLabels={REGION_FIELD_LABELS}
      />

      <AdminFormCard title="Region details">
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <LabeledTextField
              label="Region Code"
              required
              value={values.region_code}
              onChange={handleChange('region_code')}
              onBlur={handleBlur('region_code')}
              error={!!errors.region_code}
              helperText="e.g., ANZ_BNE_NORTH, US_EAST"
              disabled={isEditMode}
              inputProps={{ style: { textTransform: 'uppercase' } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <LabeledSelect
              label="Market"
              required
              value={values.market}
              onChange={handleMarketChange}
              error={!!errors.market}
              disabled={isEditMode}
            >
              <MenuItem value="ANZ">ANZ</MenuItem>
              <MenuItem value="USA">USA</MenuItem>
            </LabeledSelect>
          </Grid>
          <Grid item xs={12}>
            <LabeledTextField
              label="Region Name"
              required
              value={values.region_name}
              onChange={handleChange('region_name')}
              onBlur={handleBlur('region_name')}
              error={!!errors.region_name}
              helperText="Display name for the region"
            />
          </Grid>
          <Grid item xs={12}>
            <LabeledTextField
              label="Description"
              multiline
              rows={2}
              value={values.description}
              onChange={handleChange('description')}
              placeholder="Optional description of this region..."
            />
          </Grid>

          <Grid item xs={12}>
            <FieldGroupHeading>Default ROM/FBC Assignment</FieldGroupHeading>
            <LabeledSelect
              label="Default ROM/FBC"
              value={values.default_bdm_id || ''}
              onChange={(e) =>
                setFieldValue('default_bdm_id', e.target.value ? Number(e.target.value) : null)
              }
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {bdmOptions.map((bdm) => (
                <MenuItem key={bdm.id} value={bdm.id}>
                  {bdm.name} ({bdm.email})
                </MenuItem>
              ))}
            </LabeledSelect>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              When creating or editing a Franchise Partner in this region, this ROM/FBC will be pre-selected
            </Typography>
          </Grid>
        </Grid>
      </AdminFormCard>

      <AdminFormActions
        saveLabel={isEditMode ? 'Save Changes' : 'Create Region'}
        saving={isSubmitting}
        onSave={() => handleSubmit()}
        cancelTo="/admin/system/regions"
      />
    </Container>
  )
}

export default RegionForm
